import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useAssetResolver } from "@/react/Webshop/providers/AssetResolver";

const getRequiredSize = (size) => {
  const sizes = [
    {
      size: 160,
      width: 160,
      height: 120,
    },
    {
      size: 320,
      width: 320,
      height: 160,
    },
    {
      size: 640,
      width: 640,
      height: 480,
    },
    {
      size: 800,
      width: 800,
      height: 640,
    },
    {
      size: 1280,
      width: 1280,
      height: 720,
    },
  ];
  for (const s of sizes) {
    if (s.width > size.width || s.height > s.height) {
      return s.size;
    }
  }
  return 1280;
};

const placeHolder =
  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkqAcAAIUAgUW0RjgAAAAASUVORK5CYII=";

const EpmsImage = (props) => {
  const { uuid, size, lockedSize, watermark, onLoad, ...rest } = props;
  const imageRef = useRef();
  const [effect, setEffect] = useState(props.effect || "original");
  const [rotation, setRotation] = useState(props.rotation || 0);
  const [imageSize, setImageSize] = useState(props.size || 160);
  const [isVisible, setIsVisible] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [requiredSize, setRequiredSize] = useState(false);
  const assetResolver = useAssetResolver();

  let urlCreator = useMemo(() => {
    if (props.urlCreator) {
      return props.urlCreator;
    }
    return assetResolver.imageUrl;
  }, [props.urlCreator]);

  // This is triggered by resize observer:
  useEffect(() => {
    if (requiredSize && imageSize < requiredSize && !lockedSize) {
      setImageSize(requiredSize);
    }
  }, [requiredSize]);

  const onImageLoad = (e) => {
    console.log("EpmsImage successfully loaded", e.target.src);
    if (props.onLoad) {
      // Only propagnate onLoad if the url matches
      if (
        e.target.src ==
        urlCreator(
          props.uuid,
          size,
          props.effect,
          props.rotation,
          watermark,
          retryCount,
        )
      ) {
        props.onLoad(e);
      }
    }
    if (imageRef && e.target.src != placeHolder && !lockedSize) {
      const size = imageRef.current.getBoundingClientRect();

      const requiredSize = getRequiredSize(size);
      if (requiredSize > imageSize) {
        setImageSize(requiredSize);
      }
    }
  };

  const imageSrc = useMemo(() => {
    console.log("EpmsImage useMemo", props);
    if (!isVisible && !props.forceVisible) {
      return placeHolder;
    }

    const url = urlCreator(
      uuid,
      imageSize,
      effect,
      rotation,
      watermark,
      retryCount,
    );

    console.log("EpmsImage: New image src", url);

    return url;
  }, [isVisible, effect, imageSize, uuid, rotation, retryCount]);

  // FIXME: Should have a cancel for this. useEffect?
  const onError = (e) => {
    let sleepTime = retryCount * 3;
    if (sleepTime > 30) {
      sleepTime = 30;
    }
    console.log("On image error, try again in", sleepTime);
    setTimeout(() => {
      setRetryCount((rc) => {
        console.log("Setting new retry count to " + (rc + 1));
        return rc + 1;
      });
    }, sleepTime * 1000);
  };

  useEffect(() => {
    console.log("Prop changed to ", props);
    if (props.size > imageSize) {
      setImageSize(props.size);
    }

    if (props.effect != effect) {
      console.log("EpmsImage setting effect", props.effect);
      setEffect(props.effect);
    }
    if (props.rotation != rotation) {
      console.log("EpmsImage: Setting new rotation", props.rotation);
      setRotation(props.rotation);
    }
  }, [props.size, props.effect, props.rotation]);

  useEffect(() => {
    let observer;
    let resizeObserver;
    let didCancel = false;

    console.log("Default load");
    if (imageRef.current) {
      if (typeof IntersectionObserver != "undefined") {
        observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              // when image is visible in the viewport + rootMargin
              if (
                !didCancel &&
                (entry.intersectionRatio > 0 || entry.isIntersecting)
              ) {
                console.log("Setting real src");
                setIsVisible(true);
              }
            });
          },
          {
            threshold: 0.01,
            rootMargin: "75%",
          },
        );
        observer.observe(imageRef.current);
      } else {
        // Old browsers fallback
        setIsVisible(true);
      }

      if (typeof ResizeObserver != "undefined") {
        console.log("Resize observe");
        resizeObserver = new ResizeObserver((entries) => {
          // Use request animation frame to avoid ResizeObserver loop limit exceeded
          window.requestAnimationFrame(() => {
            if (!Array.isArray(entries) || !entries.length) {
              return;
            }
            entries.forEach((entry) => {
              // Don't use the event as it's not scaled (If we used scaled templates),
              // element getBoundingClientRect is real size:
              if (!imageRef.current) {
                return;
              }

              const size = imageRef.current.getBoundingClientRect();
              /*
                                    console.log('Resize entry', entry);
                                    const size = entry.contentRect;
                                     */
              const requiredSize = getRequiredSize(size);
              setRequiredSize(requiredSize);
            });
          });
        });
        resizeObserver.observe(imageRef.current);
      }
    }
    return () => {
      if (!imageRef.current) {
        return;
      }
      // on component unmount, we remove the listner
      if (observer && observer.unobserve) {
        observer.unobserve(imageRef.current);
      }
      if (resizeObserver && resizeObserver.unobserve) {
        observer.unobserve(imageRef.current);
      }
    };
  }, []);

  if (!uuid) {
    return null;
  }

  // Super shitty hack:/
  if (props.ref) {
    props.ref.current = imageRef.current;
  }

  console.log("EpmsImage source is now", imageSrc);

  return (
    <>
      <img
        ref={imageRef}
        src={imageSrc}
        onLoad={onImageLoad}
        onError={onError}
        {...rest}
      />
    </>
  );
};

EpmsImage.propTypes = {
  uuid: PropTypes.string.isRequired,
  effect: PropTypes.oneOf(["original", "bw", "sepia"]),
  urlCreator: PropTypes.func,
  onClick: PropTypes.func,
  size: PropTypes.number,
  className: PropTypes.string,
  style: PropTypes.object,
  forceVisible: PropTypes.bool,
};

EpmsImage.defaultProps = {
  effect: "original",
  size: 160,
  rotation: 0,
  lockedSize: false,
  watermark: true,
  forceVisible: false,
};

export default EpmsImage;
